<template>
  <div class="pa-4">
    <h2 class="ml-4 my-4">{{ $t('message.referrals_agent') }}</h2>
    <div v-if="loading" class="text-center">
      <v-skeleton-loader class="mx-auto" type="card"></v-skeleton-loader>
    </div>
    <div v-if="affiliate">
      <v-card elevation="0">
        <v-card-text class="text-center error--text text-h4" v-if="affiliateId != 'new_affiliate' && !affiliate.active">
          <div v-if="affiliate.expirationDate">{{ $t('message.expired') }}</div>
          <div v-else>{{ $t('message.inactive') }}</div>
        </v-card-text>
        <v-card-text>
          <v-list-item v-if="affiliateId != 'new_affiliate' && affiliate.user" class="mb-2"
            @click="goToMember(affiliate.user._id, affiliate.user.displayName)">
            <v-list-item-avatar>
              <ImageDisplay v-if="affiliate.user.userImageSmall" :src="affiliate.user.userImageSmall" />
              <img v-else :src="assets('default-profile-avatar.png')" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                {{ affiliate.user.displayName }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ $t('message.affiliate') }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="affiliateId != 'new_affiliate' && affiliate.manager" class="mb-2"
            @click="goToMember(affiliate.manager._id, affiliate.manager.displayName)">
            <v-list-item-avatar>
              <ImageDisplay v-if="affiliate.manager.userImageSmall" :src="affiliate.manager.userImageSmall" />
              <img v-else :src="assets('default-profile-avatar.png')" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                {{ affiliate.manager.displayName }} <v-btn class="ml-1" @click.stop="addManagerDialog = true" icon
                  small><v-icon>mdi-pencil-circle-outline</v-icon></v-btn>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ $t('message.manager') }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-else-if="affiliateId != 'new_affiliate' && !affiliate.manager" class="mb-2">
            <v-list-item-action>
              <v-icon size="36">mdi-account-tie-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ $t('message.add_manager') }} <v-btn @click="addManagerDialog = true" icon
                  small><v-icon>mdi-plus-circle-outline</v-icon></v-btn>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ $t('message.manager') }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-btn block class="mb-2" outlined v-if="affiliate.user && affiliate.user.email"
            @click="copyToClipboard(affiliate.user.email)" dense :label="$t('message.email')">
            {{ $t('message.copy_email') }}: {{ affiliate.user.email }}
          </v-btn>
          <v-text-field v-show="affiliate.code ? true : false" :disabled="true" outlined dense maxlength="50"
            :label="$t('message.code')" v-model="affiliate.code">
          </v-text-field>

          <v-combobox :label="$t('message.campaign')" v-model="affiliate.campaign" :items="campaigns"
            :hide-no-data="!campaignSearch" dense outlined small-chips :search-input.sync="campaignSearch">
            <template slot="no-data">
              <v-list-item>
                <span class="subheading">{{ $t('message.click_enter_add_new') }}:</span>
                <v-chip label small>{{ campaignSearch }}</v-chip>
              </v-list-item>
            </template>
          </v-combobox>
          <v-autocomplete v-show="affiliateId == 'new_affiliate'" :disabled="affiliate.email ? true : false"
            v-model="affiliate.user" :items="members" :label="$t('message.pick_member')" item-text="text"
            item-value="value" clearable outlined dense></v-autocomplete>
          <div v-show="affiliateId == 'new_affiliate'" class="text-center text-caption mb-2">{{ $t('message.or') }}
          </div>
          <v-text-field v-show="affiliateId == 'new_affiliate'" :disabled="affiliate.user ? true : false" outlined dense
            maxlength="50" :label="$t('message.email')" v-model="affiliate.email"></v-text-field>
          <v-textarea rows="3" outlined dense :label="$t('message.notes')" v-model="affiliate.notes"></v-textarea>
          <div>
            <div class="text-subtitle-1">{{ $t('message.available_sources') }}</div>
            <div class="text-caption">{{ $t('message.available_sources_explained') }}</div>
          </div>
          <v-card-actions class="pt-0 pb-0">
            <v-switch hide-details multiple value="contact-form" v-model="affiliate.sources"
              :label="$t('message.contact_form')"></v-switch>
            <v-btn v-show="affiliateId != 'new_affiliate'" small icon @click="copyCodeUrl('contact-form')"><v-icon
                size="20">mdi-content-copy</v-icon></v-btn>
          </v-card-actions>
          <v-card-actions class="pt-0 pb-0" v-if="stripeReady">
            <v-switch hide-details multiple value="memberships-page" v-model="affiliate.sources"
              :label="$t('message.memberships_page')"></v-switch>
            <v-btn v-show="affiliateId != 'new_affiliate'" small icon @click="copyCodeUrl('memberships-page')"><v-icon
                size="20">mdi-content-copy</v-icon></v-btn>
          </v-card-actions>
          <v-card-actions class="pt-0 pb-0">
            <v-switch hide-details multiple value="schedule-visit-page" v-model="affiliate.sources"
              :label="$t('message.schedule_visit_page')"></v-switch>
            <v-btn v-show="affiliateId != 'new_affiliate'" small icon
              @click="copyCodeUrl('schedule-visit-page')"><v-icon size="20">mdi-content-copy</v-icon></v-btn>
          </v-card-actions>
          <div class="text-subtitle-1 mt-2">{{ $t('message.expiration_date') }}</div>
          <v-row>
            <v-col>
              <DatePicker v-model="affiliate.expirationDate" :past-event="false">
              </DatePicker>
            </v-col>
            <v-col>
              <v-btn icon small v-if="affiliate.expirationDate"
                @click="affiliate.expirationDate = null"><v-icon>mdi-calendar-remove</v-icon></v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <div v-if="trendline && trendline.options">
          <ApexChart width="100%" type="area" height="200" :options="trendline.options" :series="trendline.data">
          </ApexChart>
        </div>
        <v-expansion-panels v-if="affiliateId != 'new_affiliate'" class="mt-2" flat v-model="panels">
          <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
            <v-expansion-panel-header>
              <div class="text-left">
                <v-icon>mdi-account-star-outline</v-icon>
                {{ $t("message.referrals") }} ({{ referrals ? referrals.length : 0 }})
                <span v-if="totalPaid > 0" class="ml-1 text-overline"><b>{{ affiliate.managedAcademy.currencySymbol }}{{
                  totalPaid }}</b> {{ $t("message.paid") }}</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-actions class="pt-0 pb-0">
                <v-spacer></v-spacer>
                <v-btn icon :loading="loading" @click="loadData()"><v-icon>mdi-sync</v-icon></v-btn>
                <v-btn icon @click="addReferralDialog = true"><v-icon>mdi-plus-circle-outline</v-icon></v-btn>
              </v-card-actions>
              <v-list>
                <v-list-item v-for="(item, i) in referrals" :key="i + '_referral'" @click="doNothing()">
                  <v-list-item-avatar size="25">
                    <ImageDisplay v-if="item.image" :src="item.image" />
                    <img v-else :src="assets('default-profile-avatar.png')" />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip v-if="item.completed" small color="success">{{ $t('message.converted') }}</v-chip>
                      <v-chip v-else-if="item.lost" small color="error">{{ $t('message.lost') }}</v-chip>
                      <v-chip v-else small>{{ $t('message.not_converted') }}</v-chip>
                      <span class="ml-1" v-if="item.paidAmount"><b>{{ affiliate.managedAcademy.currencySymbol }}{{
                        Math.abs(item.paidAmount) }}</b> {{ $t("message.paid") }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action @click.stop="showComments(item)">
                    <v-badge v-if="item.memberData && item.memberData.comments && item.memberData.comments.length"
                      color="success" :content="item.memberData.comments.length" overlap>
                      <v-icon>mdi-comment-text-multiple-outline</v-icon>
                    </v-badge>
                    <v-icon v-else>mdi-comment-text-multiple-outline</v-icon>
                  </v-list-item-action>
                  <v-list-item-action>
                    <v-menu offset-y>
                      <template v-slot:activator="{ on }">
                        <v-btn :disabled="!item.memberDataId ? true : false" v-on="on" icon small slot="activator" left>
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-item
                          @click="navigateTo({ name: 'viewvisitor', params: { visitorId: item.memberDataId }, crumbName: item.name || item.email })">
                          <v-list-item-title>
                            {{ $t("message.view_referral") }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="!item.completed" @click.stop="showReferralOptions(item)">
                          <v-list-item-title>
                            {{ $t("message.mark_converted") }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="!item.completed" @click.stop="showReferralOptions(item)">
                          <v-list-item-title>
                            {{ $t("message.mark_lost") }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="!item.completed" @click.stop="showReferralOptions(item)">
                          <v-list-item-title>
                            {{ $t("message.remove") }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click.stop="showReferralOptions(item)">
                          <v-list-item-title>
                            {{ $t("message.transactions") }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="item.email" @click.stop="copyToClipboard(item.email)">
                          <v-list-item-title>
                            {{ $t("message.copy_email") }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
            <v-expansion-panel-header>
              <div class="text-left">
                <v-icon>mdi-cursor-default-click-outline</v-icon>
                {{ $t("message.interactions") }}
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-actions class="pt-0 pb-0">
                <v-spacer></v-spacer>
                <v-btn icon :loading="loading" @click="loadData()"><v-icon>mdi-sync</v-icon></v-btn>
              </v-card-actions>
              <v-list-item v-for="(item, i) in interactions" :key="i + '_interaction'">
                <v-list-item-content>
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small color="success">{{ item.count }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel @click="getSubAffiliates()" v-if="subAffiliatesCount"
            :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
            <v-expansion-panel-header>
              <div class="text-left">
                <v-icon>mdi-account-group-outline</v-icon>
                {{ $t("message.affiliates") }} ({{ subAffiliatesCount }})
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list>
                <v-list-item @click="goToAffiliate(item._id)" v-for="(item, i) in subAffiliates"
                  :key="i + '_subaffiliate'">
                  <v-list-item-avatar>
                    <MemberAvatar :image="item.user.userImageSmall"></MemberAvatar>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.user.displayName }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.campaign }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-badge :content="item.referrals && item.referrals.length ? item.referrals.length : '0'"
                      color="error" overlap>
                      <v-icon>mdi-account-star-outline</v-icon>
                    </v-badge>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-card-actions v-if="!isSmallerDisplay()">
          <v-btn outlined :loading="addingAffiliate"
            :disabled="(!affiliate.email && !affiliate.user) || !affiliate.campaign" color="primary"
            v-if="affiliateId == 'new_affiliate'" small elevation="0" @click="addAffiliate()">{{
              $t('message.add')
            }}</v-btn>
          <v-btn outlined color="primary" v-else small elevation="0" @click="updateAffiliate()">{{ $t('message.update')
            }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" v-if="affiliateId != 'new_affiliate' && affiliate.active" small elevation="0"
            @click="removeAffiliate()">{{ $t('message.remove') }}</v-btn>
          <v-btn outlined color="primary" v-else-if="affiliateId != 'new_affiliate' && !affiliate.active" small
            elevation="0" @click="removeAffiliate(true)">{{ $t('message.activate') }}</v-btn>
          <v-btn small elevation="0" @click="goBack()">{{ $t('message.back') }}</v-btn>
        </v-card-actions>
        <div v-else>
          <v-btn outlined :disabled="!affiliate.email && !affiliate.user" block color="primary"
            v-if="affiliateId == 'new_affiliate'" small elevation="0" @click="addAffiliate()">{{ $t('message.add')
            }}</v-btn>
          <v-btn outlined class="mt-1" block color="primary" v-else small elevation="0" @click="updateAffiliate()">{{
            $t('message.update') }}</v-btn>
          <v-btn class="mt-1" block color="error" v-if="affiliateId != 'new_affiliate' && affiliate.active" small
            elevation="0" @click="removeAffiliate()">{{ $t('message.remove') }}</v-btn>
          <v-btn class="mt-1" block outlined color="primary"
            v-else-if="affiliateId != 'new_affiliate' && !affiliate.active" small elevation="0"
            @click="removeAffiliate(true)">{{ $t('message.activate') }}</v-btn>
          <v-btn class="mt-1" block small elevation="0" @click="goBack()">{{ $t('message.back') }}</v-btn>
        </div>
      </v-card>
    </div>
    <v-dialog v-model="referralOptionsDialog" max-width="90%">
      <v-card class="pa-2" outlined v-if="selectedReferral">
        <v-card-title>{{ selectedReferral.name }}</v-card-title>
        <v-card-text>
          <div v-if="selectedReferral.completed"><b>{{ $t('message.converted') }}:</b> <span>{{ $d(new
            Date(selectedReferral.completed), "month_day_year", $i18n.locale) }}</span></div>
          <div><b>{{ $t('message.paid') }}:</b> <span>{{ affiliate.managedAcademy.currencySymbol }}{{
            Math.abs(selectedReferral.paidAmount) }}</span></div>
          <b>{{ $t('message.payments') }}:</b>
          <v-list v-if="selectedReferral.payments">
            <v-list-item v-for="(item, i) in selectedReferral.payments" :key="i + '_transaction'">
              <v-list-item-content>
                <v-list-item-title>{{ affiliate.managedAcademy.currencySymbol }}{{ Math.abs(item.amount) }} - {{ $d(new
                  Date(item.time), "month_day_year", $i18n.locale) }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.notes">{{ item.notes }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon @click="goToExpense(item._id)">mdi-card-search-outline</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <div v-else-if="loadingPayments">
            <v-skeleton-loader class="mx-auto" type="list-item"></v-skeleton-loader>
          </div>
          <v-divider></v-divider>
          <div class="mt-2">
            <v-text-field hide-details outlined dense :label="$t('message.paid_amount')"
              v-model="paidAmount"></v-text-field>
            <v-switch v-if="paidAmount > 0" dense :label="$t('message.add_expense')" v-model="addExpense">
            </v-switch>
            <v-textarea v-if="addExpense" rows="3" outlined dense :label="$t('message.notes')"
              v-model="paidAmountNotes"></v-textarea>
          </div>
        </v-card-text>
        <v-card-actions v-if="!isSmallerDisplay()">
          <v-btn outlined :loading="processingReferralAction" :disabled="paidAmount <= 0" color="primary" elevation="0"
            @click.stop="updateReferral()">{{ $t('message.add_payment') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn outlined :loading="processingReferralAction"
            v-if="!selectedReferral.completed && !selectedReferral.cancelled" small color="primary" elevation="0"
            @click.stop="updateReferral(true, { converted: true })">{{ $t('message.mark_converted') }}</v-btn>
          <v-btn outlined :loading="processingReferralAction"
            v-if="!selectedReferral.completed && !selectedReferral.cancelled" small color="primary" elevation="0"
            @click.stop="updateReferral(true, { lost: true })">{{ $t('message.mark_lost') }}</v-btn>
          <v-btn :loading="processingReferralAction" v-if="!selectedReferral.completed && !selectedReferral.cancelled"
            small color="error" elevation="0" @click.stop="updateReferral(true, { cancelled: true })">{{
              $t('message.remove')
            }}</v-btn>
          <v-btn elevation="0" @click.stop="referralOptionsDialog = null; selectedReferral = null;">{{
            $t('message.close') }}</v-btn>
        </v-card-actions>
        <div v-else>
          <v-btn block outlined :loading="processingReferralAction" :disabled="paidAmount <= 0" color="primary"
            elevation="0" @click.stop="updateReferral()">{{ $t('message.add_payment') }}</v-btn>
          <v-btn class="mt-1" block outlined :loading="processingReferralAction"
            v-if="!selectedReferral.completed && !selectedReferral.cancelled" small color="primary" elevation="0"
            @click.stop="updateReferral(true, { converted: true })">{{ $t('message.mark_converted') }}</v-btn>
          <v-btn class="mt-1" block outlined :loading="processingReferralAction"
            v-if="!selectedReferral.completed && !selectedReferral.cancelled" small color="primary" elevation="0"
            @click.stop="updateReferral(true, { lost: true })">{{ $t('message.mark_lost') }}</v-btn>
          <v-btn class="mt-1" block :loading="processingReferralAction"
            v-if="!selectedReferral.completed && !selectedReferral.cancelled" small color="error" elevation="0"
            @click.stop="updateReferral(true, { cancelled: true })">{{ $t('message.remove') }}</v-btn>
          <v-btn class="mt-1" block elevation="0"
            @click.stop="referralOptionsDialog = null; selectedReferral = null;">{{
              $t('message.close') }}</v-btn>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="addManagerDialog" max-width="90%">
      <v-card class="pa-2" outlined>
        <v-card-title>{{ $t('message.add_manager') }}</v-card-title>
        <v-card-text>
          <v-text-field v-show="affiliateId != 'new_affiliate'" outlined dense maxlength="50"
            :label="$t('message.email')" v-model="managerEmail"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined color="primary" elevation="0" @click.stop="addManager()">{{ $t('message.add') }}</v-btn>
          <v-btn elevation="0" @click.stop="addManagerDialog = null;">{{ $t('message.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="addReferralDialog" max-width="90%">
      <v-card class="pa-2" outlined>
        <v-card-title>{{ $t('message.add_referral') }}</v-card-title>
        <v-card-actions>
          <v-switch :label="$t('message.new_prospect')" v-model="newProspectSwitch"></v-switch>
        </v-card-actions>
        <v-card-text>
          <VisitorsAutocomplete :label="$t('message.existing_visitor')" v-model="newReferralUserVisitor" />
          <MembersAutocomplete :label="$t('message.existing_member')" v-model="newReferralUserMember" />
          <div v-if="newProspectSwitch">
            <v-card flat outlined>
              <v-card-text>
                <v-text-field class="mt-6" :label="$t('message.name') + '*'" v-model="newProspect.name"
                  autocomplete="false" required outlined dense></v-text-field>
                <v-text-field label="Email*" v-model="newProspect.email" required outlined dense></v-text-field>
                <GenderSelect :icon="false" v-model="newProspect.gender" />
                <CountriesSelect :show-icon="false" v-model="newProspect.nationality" />
                <v-text-field :label="$t('message.phone')" v-model="newProspect.phone" autocomplete="false" required
                  outlined dense></v-text-field>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="newProspectSwitch" :loading="addingReferral" outlined color="primary" elevation="0"
            @click.stop="addNewProspect()">{{
              $t('message.add') }}</v-btn>
          <v-btn v-else :loading="addingReferral" outlined color="primary" elevation="0" @click.stop="addReferral()">{{
            $t('message.add') }}</v-btn>
          <v-btn elevation="0" @click.stop="addReferralDialog = null;">{{ $t('message.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="commentsDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card v-if="selectedReferral && selectedReferral.memberData">
        <v-toolbar flat class="opposite--text" color="primary" dense>
          <v-toolbar-title>
            {{ $t('message.comments') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="opposite" small icon @click="commentsDialog = false">
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <div>
          <MemberComments :hide-buttons="true" :data="selectedReferral.memberData" view="affiliate" />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MemberAvatar from "@/components/displays/MemberAvatar.vue";
import MemberComments from "@/components/displays/MemberComments.vue";
import CountriesSelect from "@/components/inputs/CountriesSelect.vue";
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import GenderSelect from "@/components/inputs/GenderSelect.vue";
import VisitorsAutocomplete from "@/components/inputs/VisitorsAutocomplete.vue";
import MembersAutocomplete from "@/components/inputs/MembersAutocomplete.vue";
import Api from "@/services/Api";
import ApexChart from 'vue-apexcharts';

export default {
  data() {
    return {
      affiliateId: this.$route.params.id,
      campaignName: this.$route.query.c,
      affiliate: null,
      loading: true,
      members: [],
      referrals: [],
      campaigns: [],
      interactions: [],
      addingAffiliate: false,
      managerEmail: null,
      campaignSearch: null,
      panels: null,
      trendline: null,
      referralOptionsDialog: null,
      selectedReferral: null,
      paidAmount: 0,
      addManagerDialog: null,
      addExpense: false,
      stripeReady: false,
      sources: [],
      commentsDialog: null,
      loadingPayments: false,
      totalPaid: 0,
      paidAmountNotes: null,
      processingReferralAction: false,
      addReferralDialog: null,
      addingReferral: false,
      newReferralUserMember: null,
      newReferralUserVisitor: null,
      newProspectSwitch: false,
      newProspect: {},
      subAffiliatesCount: 0,
      subAffiliates: null,
    };
  },
  components: {
    DatePicker,
    MemberComments,
    VisitorsAutocomplete,
    MembersAutocomplete,
    CountriesSelect,
    GenderSelect,
    MemberAvatar,
    ApexChart,
  },
  beforeRouteUpdate(to, from, next) {
    this.affiliateId = to.params.id;
    console.log('Now to: ', this.affiliateId);
    this.subAffiliates = null;
    this.subAffiliatesCount = null;
    this.affiliate = null;
    this.loading = null;
    this.loadData();
    next();
  },
  created() {
    if (this.affiliateId == 'new_affiliate') {
      this.loading = false;
      this.affiliate = {
        campaign: this.campaignName ? this.campaignName : null
      };
      Api()
        .get("admins/academy/" + this.$store.state.selectedAcademy + "/members")
        .then((response) => {
          const data = response.data;
          if (data) {
            for (const m of data.approved) {
              this.members.push({ value: m._id, text: m.fName + " " + m.lName });
            }
          }
        });
    } else {
      this.loadData();
    }
    this.getAvailableCampaigns();
    Api()
      .get(
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/is_connected/stripe"
      )
      .then((response) => {
        this.stripeReady = response.data.stripeConnected;
      });
  },
  methods: {
    goToAffiliate(id) {
      console.log('going to: ', id)
      if (id)
        this.navigateTo({
          name: 'manageaffiliate',
          params: { id: id },
        })
    },
    getSubAffiliates() {
      this.loadingSubAffiliates = true;
      Api()
        .get("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliate/" + this.affiliateId + "/sub-affiliates")
        .then((res) => {
          this.subAffiliates = res.data.list;
          this.loadingSubAffiliates = false;
        }).catch(() => {
          this.loadingSubAffiliates = false;
        });
    },
    addReferral(newPropsectId) {
      this.addingReferral = true;
      let newReferralUser = this.newReferralUserMember;
      if (!newReferralUser)
        newReferralUser = this.newReferralUserVisitor;
      Api()
        .patch("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliate/" + this.affiliateId + "/user/" + (newPropsectId || newReferralUser))
        .then(() => {
          this.addReferralDialog = false;
          this.showAlert('success', this.$t('message.updated'));
          this.loadData();
          this.addingReferral = false;
        }).catch((err) => {
          this.addingReferral = false;
          if (err && err.response.data.error)
            this.showAlert('error', err.response.data.error);
          else
            this.showAlert('error', this.$t('message.oops_not_working'));
        });
    },
    addNewProspect() {
      this.addingReferral = true;
      if (!this.newProspect.name || !this.newProspect.email) {
        this.showAlert("error", "Oops!", this.$t("message.fill_all_params"));
      } else {
        Api()
          .post(
            "admins/academy/" + this.$store.state.managedAcademy + "/prospects", this.newProspect
          )
          .then((response) => {
            if (response.data) {
              this.newProspect = {};
              return this.addReferral(response.data.newEntry._id);
            }
          });
      }
    },
    addManager() {
      const email = this.managerEmail;
      if (email) {
        Api()
          .patch("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliate/" + this.affiliateId + "/manager", { email: email })
          .then(() => {
            this.addManagerDialog = false;
            this.showAlert('success', this.$t('message.updated'));
            this.loadData();
          }).catch((err) => {
            if (err && err.response.data.error)
              this.showAlert('error', err.response.data.error);
            else
              this.showAlert('error', this.$t('message.oops_not_working'));
          });
      }
      console.log(email)
    },
    copyCodeUrl(source) {
      for (const i of this.sources) {
        if (i.source == source)
          this.copyToClipboard(i.link);
      }
    },
    goToExpense(i) {
      this.navigateTo({ name: 'manageexpense', params: { transactionId: i } });
    },
    updateReferral(skipExpense, data) {
      this.processingReferralAction = true;
      console.log(this.selectedReferral)
      if (!this.selectedReferral || !this.selectedReferral.memberData) {
        this.showAlert('error', this.$t('message.inavlid'));
        return;
      }
      if (!skipExpense && isNaN(this.paidAmount)) {
        this.showAlert('error', this.$t('message.not_a_number'));
        return;
      }
      let postData = {
        paidAmount: -1 * this.paidAmount,
        addExpense: skipExpense ? false : this.addExpense,
        affiliateId: this.affiliate._id,
        notes: this.paidAmountNotes,
      };
      if (data) {
        if (data.lost)
          postData.lost = true;
        else if (data.cancelled)
          postData.cancelled = true;
        else if (data.converted)
          postData.converted = true;
      }
      if (this.addExpense) {
        postData.expense = {
          items: [
            {
              name: this.$t('message.affiliate_payment') + ": " + this.affiliate.user.displayName,
              amountDecimal: this.totalExpense,
              currency: this.affiliate.managedAcademy.currency,
              quantity: 1,
              description: this.$t('message.referral') + ": " + this.selectedReferral.name
            },
          ],
          quantity: 1,
          completed: new Date(),
          processedBy: "desk",
          tags: ['referrals']
        }
      }
      Api()
        .patch("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliate/" + this.affiliateId + "/referral/" + this.selectedReferral.memberData._id + "/complete", postData)
        .then(() => {
          this.loadData();
          this.referralOptionsDialog = false;
          this.showAlert('success', this.$t('message.updated'));
          this.addExpense = false;
          this.paidAmount = 0;
          this.processingReferralAction = false;
        }).catch(() => {
          this.showAlert('error', this.$t('message.oops_not_working'));
          this.processingReferralAction = false;
        });
    },
    showReferralOptions(i) {
      this.referralOptionsDialog = true;
      this.selectedReferral = i;
      this.loadingPayments = true;
      Api()
        .get("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliates/referrals/" + i._id)
        .then((response) => {
          this.selectedReferral.payments = response.data.referral.payments;
          this.loadingPayments = false;
        });
    },
    showComments(i) {
      this.commentsDialog = true;
      this.selectedReferral = i;
    },
    getAvailableCampaigns() {
      Api()
        .get("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliates/campaigns")
        .then((response) => {
          const data = response.data;
          if (data)
            this.campaigns = response.data.data;
        });
    },
    removeAffiliate(activate) {
      Api()
        .patch("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliate/" + this.affiliateId + "/active/toggle", { active: activate })
        .then(() => {
          this.affiliate.active = activate;
        }).catch(() => {
          this.showAlert('error', this.$t('message.oops_not_working'));
        });
    },
    loadData() {
      this.loading = true;
      Api()
        .get("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliate/" + this.affiliateId)
        .then((response) => {
          let tmp = response.data.affiliate;
          if (tmp.expirationDate)
            tmp.expirationDate = new Date(tmp.expirationDate);
          this.affiliate = tmp;
          this.totalPaid = response.data.totalPaid;
          this.sources = response.data.sources;
          this.referrals = response.data.referrals;
          this.interactions = response.data.interactions;
          this.subAffiliatesCount = response.data.subAffiliatesCount;
          if (response.data.trendline && response.data.trendline.dates && response.data.trendline.dates.length)
            this.loadTrendlineData(response.data.trendline);
          this.loading = false;
        }).catch((e) => {
          console.log(e)
          this.showAlert('error', this.$t('message.oops_not_working'));
        });
    },
    addAffiliate() {
      this.addingAffiliate = true;
      Api()
        .post("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliate", this.affiliate)
        .then(() => {
          this.navigateTo({
            name: "growthcentraltab",
            params: { tabId: 5 },
          });
        }).catch(err => {
          this.addingAffiliate = false;
          if (err && err.response.data.error)
            this.showAlert('error', err.response.data.error);
          else
            this.showAlert('error', this.$t('message.oops_not_working'));
        });
    },
    updateAffiliate() {
      Api()
        .patch("referrals/managedacademy/" + this.$store.state.managedAcademy + "/affiliate/" + this.affiliateId, this.affiliate)
        .then(() => {
          this.showAlert("success", this.$t("message.updated"));
        });
    },
    loadTrendlineData(data) {
      this.trendline = {};
      this.trendline.data = [
        {
          name: data.seriesLabels[0],
          data: data.seriesData[0]
        },
        {
          name: data.seriesLabels[1],
          data: data.seriesData[1]
        },
      ];
      //Set up the dates:
      const categories = [];
      for (const i of data.dates) {
        const d = new Date();
        d.setDate(i.day);
        d.setMonth(i.month);
        d.setFullYear(i.year);
        d.setHours(0);
        d.setMinutes(0);
        categories.push(d)
      }
      this.trendline.options = {
        colors: [this.themeColor('primary'), this.themeColor('orange')],
        xaxis: {
          categories: categories,
          labels: {
            show: false,
          }
        },
        yaxis: {
          labels: {
            show: false,
          }
        },
        tooltip: {
          x: {
            format: 'dd/MM/yy'
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth'
        },
        theme: {
          mode: this.isDarkMode() ? 'dark' : 'light',
        },
        chart: {
          toolbar: {
            show: false
          },
          background: 'none'
        },
        legend: {
          position: 'bottom'
        },
        grid: {
          borderColor: this.themeColor('gray'),
          yaxis: {
            lines: {
              show: false  //or just here to disable only x axis grids
            }
          },
        },
      };
    }
  }
};
</script>

<style scoped></style>